import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl, config } from '../config';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot' | 'error';
  timestamp: Date;
}

interface Profession {
  id?: string;
  'Profession Name': string;
  'Study Field': string;
  'Profession Description': string;
  'Admission Requirements': string;
  'Relevant Institutions': string;
  'Additional Information Links': string;
  'Tags': string;
  'Teaching Languages': string;
  'Notes': string;
  score?: number;
  ai_score?: number;
  combined_score?: number;
  confidence?: string;
}

interface AIResponse {
  professions: Profession[];
  ai_response: string;
  user_analysis: {
    interests: string[];
    skills: string[];
    personality_traits: string[];
    work_preferences: string[];
    confidence_score: number;
  };
  confidence: number;
  method: string;
  total_found: number;
}

const Chat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      text: 'Hello! I\'m EduBot, your smart career advisor 🤖\n\nI\'m here to help you find a suitable profession based on your preferences.\n\nExample questions:\n• "I love drawing"\n• "I\'m interested in coding and programming"\n• "I love helping people"\n• "I\'m interested in beauty and makeup"',
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<Array<{role: string, content: string}>>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatMessagesRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Scroll to bottom when messages change
    scrollToBottom();
    
    // Auto-focus textarea after bot responses (when not loading)
    if (!isLoading && textareaRef.current) {
      // Small delay to ensure the UI has updated
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [messages, isLoading]);

  useEffect(() => {
    // Ensure initial message is visible
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    // Auto-focus the textarea when component mounts
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const generateId = () => {
    return Math.random().toString(36).substr(2, 9);
  };

  const sendMessage = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: generateId(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Add to conversation history
    const updatedHistory = [...conversationHistory, { role: 'user', content: inputValue }];
    setConversationHistory(updatedHistory);

    // Check for special commands
    const inputLower = inputValue.toLowerCase();
    if (inputLower.includes('show me all professions') || inputLower.includes('all professions') || inputLower.includes('all options')) {
      await handleShowAllProfessions();
      return;
    }
    
    if (inputLower.includes('show me more') || inputLower.includes('more professions') || inputLower.includes('more options')) {
      await handleShowMoreProfessions();
      return;
    }

    try {
      // Use the new AI-enhanced API
      const response = await axios.post(getApiUrl(config.ADVICE_ENDPOINT), null, {
        params: {
          query: inputValue,
          context: JSON.stringify(updatedHistory)
        },
        timeout: config.REQUEST_TIMEOUT
      });

      const responseData = response.data;
      const aiData: AIResponse = responseData.data || responseData;
      
      let botResponse = '';
      
      if (aiData.professions && aiData.professions.length > 0) {
        botResponse = `${aiData.ai_response}\n\n`;
        
        if (aiData.professions.length === 1) {
          const profession = aiData.professions[0];
          botResponse += `**${profession['Profession Name']}**\n`;
          botResponse += `Field: ${profession['Study Field']}\n`;
          botResponse += `Description: ${profession['Profession Description']}\n`;
          botResponse += `Requirements: ${profession['Admission Requirements'] || 'Not specified'}\n`;
          botResponse += `Institutions: ${profession['Relevant Institutions'] || 'Not specified'}\n`;
          botResponse += `Tags: ${profession['Tags'] || 'Not specified'}`;
        } else {
          const displayedProfessions = aiData.professions.slice(0, 5);
          botResponse += `**Profession Recommendations (${displayedProfessions.length} out of ${aiData.total_found} found):**\n\n`;
          displayedProfessions.forEach((profession, index) => {
            botResponse += `${index + 1}. **${profession['Profession Name']}**\n`;
            botResponse += `   Field: ${profession['Study Field']}\n`;
            botResponse += `   Accuracy: ${profession.confidence || 'Medium'}\n\n`;
          });
          
          // Add option to see all professions if there are more than 5
          if (aiData.total_found > 5) {
            botResponse += `\n**💡 Want to see more?**\n`;
            botResponse += `• Type "show me all professions" to see all ${aiData.total_found} options\n`;
            botResponse += `• Type "show me more" to see 5 more professions\n`;
            botResponse += `• Or just keep asking questions to get more tailored recommendations\n`;
          }
        }
        
        if (aiData.user_analysis && aiData.user_analysis.confidence_score > 0.5) {
          botResponse += `\n\n**Your Preferences Analysis:**\n`;
          if (aiData.user_analysis.interests.length > 0) {
            botResponse += `Interests: ${aiData.user_analysis.interests.join(', ')}\n`;
          }
          if (aiData.user_analysis.skills.length > 0) {
            botResponse += `Skills: ${aiData.user_analysis.skills.join(', ')}\n`;
          }
          if (aiData.user_analysis.personality_traits.length > 0) {
            botResponse += `Personality Traits: ${aiData.user_analysis.personality_traits.join(', ')}\n`;
          }
        }
        
        // Add AI capabilities info
        if (responseData.ai_capabilities) {
          botResponse += `\n\n**🤖 AI Capabilities Used:**\n`;
          if (responseData.ai_capabilities.machine_learning) {
            botResponse += `• Machine Learning - Machine learning models\n`;
          }
          if (responseData.ai_capabilities.nlp_analysis) {
            botResponse += `• NLP - Natural Language Processing\n`;
          }
          if (responseData.ai_capabilities.deep_learning) {
            botResponse += `• Deep Learning - Neural Networks\n`;
          }
          if (responseData.ai_capabilities.semantic_search) {
            botResponse += `• Semantic Search - Advanced semantic search\n`;
          }
        }
      } else {
        botResponse = aiData.ai_response || 'Sorry, I couldn\'t find suitable professions. Could you try to provide more details about your preferences?';
      }

      const botMessage: Message = {
        id: generateId(),
        text: botResponse,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
      
      // Add bot response to conversation history
      setConversationHistory(prev => [...prev, { role: 'assistant', content: botResponse }]);

    } catch (error: any) {
      console.error('Error sending message:', error);
      
      let errorText = 'Sorry, an error occurred while communicating with the server. Please try again later.';
      
      if (error.code === 'ECONNABORTED') {
        errorText = 'The request took too long. Please try again.';
      } else if (error.response?.status === 404) {
        errorText = 'Server not found. Please check that the server is running.';
      } else if (error.response?.status >= 500) {
        errorText = 'Server error. Please try again later.';
      } else if (error.response?.status >= 400) {
        errorText = 'Bad request. Please try again.';
      } else if (error.message?.includes('Network Error')) {
        errorText = 'Network communication error with the server. Please try again later.';
      }
      
      const errorMessage: Message = {
        id: generateId(),
        text: errorText,
        sender: 'error',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };



  const handleWheel = (e: React.WheelEvent) => {
    const chatHistory = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = chatHistory;
    const delta = e.deltaY;
    const scrollStep = 50;
    
    if (delta > 0 && scrollTop < scrollHeight - clientHeight) {
      chatHistory.scrollTop += scrollStep;
    } else if (delta < 0 && scrollTop > 0) {
      chatHistory.scrollTop -= scrollStep;
    }
  };

  const clearChat = () => {
    if (window.confirm('Are you sure you want to clear the chat?')) {
      setMessages([
        {
          id: 'welcome',
          text: 'Hello! I\'m EduBot, your smart career advisor 🤖\n\nI\'m here to help you find a suitable profession based on your preferences.\n\nExample questions:\n• "I love drawing"\n• "I\'m interested in coding and programming"\n• "I love helping people"\n• "I\'m interested in beauty and makeup"',
          sender: 'bot',
          timestamp: new Date()
        }
      ]);
      setConversationHistory([]);
      setInputValue('');
    }
  };

  const handleShowAllProfessions = async () => {
    try {
      // Get the last query from conversation history
      const lastUserMessage = conversationHistory
        .filter(msg => msg.role === 'user')
        .pop();
      
      if (!lastUserMessage) {
        const errorMessage: Message = {
          id: generateId(),
          text: 'Sorry, I couldn\'t find a previous question. Please ask a new question.',
          sender: 'error',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
        return;
      }

      // Call API to get all professions
      const response = await axios.get(getApiUrl(config.SEARCH_ENDPOINT), {
        params: {
          query: lastUserMessage.content,
          limit: 100
        },
        timeout: config.REQUEST_TIMEOUT
      });

      const allProfessions = response.data;
      
      let botResponse = `**All Relevant Professions (${allProfessions.length} professions):**\n\n`;
      
      allProfessions.forEach((profession: any, index: number) => {
        botResponse += `${index + 1}. **${profession['Profession Name']}**\n`;
        botResponse += `   Field: ${profession['Study Field']}\n`;
        botResponse += `   Accuracy: ${profession.confidence || profession.score || 'Medium'}\n\n`;
      });

      const botMessage: Message = {
        id: generateId(),
        text: botResponse,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
      setConversationHistory(prev => [...prev, { role: 'assistant', content: botResponse }]);

    } catch (error: any) {
      console.error('Error getting all professions:', error);
      const errorMessage: Message = {
        id: generateId(),
        text: 'Sorry, an error occurred while getting all professions. Please try again.',
        sender: 'error',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowMoreProfessions = async () => {
    try {
      // Get the last query from conversation history
      const lastUserMessage = conversationHistory
        .filter(msg => msg.role === 'user')
        .pop();
      
      if (!lastUserMessage) {
        const errorMessage: Message = {
          id: generateId(),
          text: 'Sorry, I couldn\'t find a previous question. Please ask a new question.',
          sender: 'error',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
        return;
      }

      // Call API to get more professions
      const response = await axios.get(getApiUrl(config.SEARCH_ENDPOINT), {
        params: {
          query: lastUserMessage.content,
          limit: 10
        },
        timeout: config.REQUEST_TIMEOUT
      });

      const moreProfessions = response.data.slice(5, 10); // Get next 5 professions
      
      if (moreProfessions.length === 0) {
        const botMessage: Message = {
          id: generateId(),
          text: 'No more professions to display. Try asking a different question or provide more details about your preferences.',
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);
        return;
      }

      let botResponse = `**More Relevant Professions:**\n\n`;
      
      moreProfessions.forEach((profession: any, index: number) => {
        botResponse += `${index + 6}. **${profession['Profession Name']}**\n`;
        botResponse += `   Field: ${profession['Study Field']}\n`;
        botResponse += `   Accuracy: ${profession.confidence || profession.score || 'Medium'}\n\n`;
      });

      const botMessage: Message = {
        id: generateId(),
        text: botResponse,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
      setConversationHistory(prev => [...prev, { role: 'assistant', content: botResponse }]);

    } catch (error: any) {
      console.error('Error getting more professions:', error);
      const errorMessage: Message = {
        id: generateId(),
        text: 'Sorry, an error occurred while getting more professions. Please try again.',
        sender: 'error',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const getSenderIcon = (sender: string) => {
    switch (sender) {
      case 'user':
        return '👤';
      case 'bot':
        return '🤖';
      case 'error':
        return '⚠️';
      default:
        return '🤖';
    }
  };

  const getSenderName = (sender: string) => {
    switch (sender) {
      case 'user':
        return 'You';
      case 'bot':
        return 'EduBot';
      case 'error':
        return 'Error';
      default:
        return 'EduBot';
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-messages" ref={chatMessagesRef} onWheel={handleWheel}>
        {messages.length === 1 && messages[0].id === 'welcome' ? (
          <div className="welcome-message">
            <p>Hello! I'm EduBot, your smart career advisor 🤖</p>
            <p>I'm here to help you find a suitable profession based on your preferences.</p>
            <p><strong>Example questions:</strong></p>
            <ul>
              <li>I love drawing</li>
              <li>I'm interested in coding and programming</li>
              <li>I love helping people</li>
              <li>I'm interested in beauty and makeup</li>
            </ul>
          </div>
        ) : (
          messages.map((message) => (
            <div key={message.id} className={`message ${message.sender}`}>
              <div className="message-avatar">
                {getSenderIcon(message.sender)}
              </div>
              <div className="message-content">
                <div className="message-name">
                  {getSenderName(message.sender)}
                </div>
                <div className="message-text">
                  {message.text.split('\n').map((line, index) => (
                    <div key={index}>
                      {line.includes('**') ? (
                        <span dangerouslySetInnerHTML={{
                          __html: line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        }} />
                      ) : (
                        line
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))
        )}
        
        {isLoading && (
          <div className="message bot">
            <div className="message-avatar">
              🤖
            </div>
            <div className="message-content">
              <div className="message-name">
                EduBot
              </div>
              <div className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      <form onSubmit={sendMessage} className="input-container">
        <textarea
          ref={textareaRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your question or preferences here... (e.g., I love drawing, I'm interested in coding)"
          disabled={isLoading}
          rows={3}
        />
        <div className="button-row">
          <button type="submit" disabled={isLoading || !inputValue.trim()}>
            {isLoading ? 'Sending...' : 'Send Message'}
          </button>
          <button 
            type="button" 
            onClick={clearChat}
            className="clear-button"
            title="Clear chat"
          >
            Clear Chat
          </button>
        </div>
        <div className="input-hint">
          💡 <strong>Tip:</strong> The more you tell me about yourself, the better recommendations I can give you
        </div>
      </form>
    </div>
  );
};

export default Chat;
